import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack } from "@mui/material"
import { isAfter, isValid } from "date-fns"
import { useState } from "react"
import { NumberField } from "../../components/Fields/NumberField"
import { NexDateTimeField } from "../../components/NexDateTimeField"
import { Label } from "../../components/Typography/Typography"
import { useMissions } from "../../hooks/useMissions"
import { Sortie } from "../../models/Sortie"
import { MissionUtils } from "../../utils/MissionUtils"

interface SortieEditorDialogProps {
    isEditDialogOpen: boolean
    setCloseDialog: (event?: object, reason?: string) => void
    sortie: Sortie
    isCreatingSortie: boolean
}

export const SortieEditorDialog = (props: SortieEditorDialogProps) => {
    const { isEditDialogOpen, setCloseDialog, sortie, isCreatingSortie } = props
    const [dialogSortie, setDialogSortie] = useState<Sortie>(sortie)
    const { handleMissionDataChange, missions } = useMissions()
    const allSorties = missions.selectedMission.sorties

    const isSortieValid = (checkSortie: Sortie) => {
        // Check sortie for certain information
        const validSortie: Partial<Record<keyof Sortie, boolean>> = {
            fe_start_sortie: isValid(checkSortie.fe_start_sortie),
            fe_stop_sortie: isAfter(checkSortie.fe_stop_sortie, checkSortie.fe_start_sortie),
            duration: (MissionUtils.calculateSortieDuration(checkSortie) ?? 0) > 0,
            stop_hobbs:
                checkSortie.stop_hobbs && checkSortie.start_hobbs
                    ? checkSortie.stop_hobbs > checkSortie.start_hobbs
                    : false,
            mob_hobbs: !!(checkSortie.mob_hobbs && checkSortie.mob_hobbs >= 0),
        }

        // If above object has a false boolean - sortie is invalid; return true
        // If obj is all true booleans; return true
        return !Object.values(validSortie).includes(false)
    }

    const handleUpdateSortie = <T extends keyof Sortie>(field: T, value: Sortie[T]) => {
        setDialogSortie({ ...dialogSortie, [field]: value })
    }

    const handleHobbsChange = <T extends keyof Sortie>(field: T, value: Sortie[T]) => {
        let mobHobbs: number | undefined = undefined
        const val = typeof value === "number" ? value : 0

        const startHobbs = field === "start_hobbs" ? val : dialogSortie.start_hobbs
        const stopHobbs = field === "stop_hobbs" ? val : dialogSortie.stop_hobbs
        const activeHobbs = field === "active_hobbs" ? val : dialogSortie.active_hobbs

        if (startHobbs != null && stopHobbs != null && activeHobbs != null) {
            mobHobbs = stopHobbs - startHobbs - activeHobbs
            mobHobbs = Math.round(mobHobbs * 10) / 10
        }

        setDialogSortie({ ...dialogSortie, [field]: value, mob_hobbs: mobHobbs })
    }

    const handleSaveSortie = () => {
        const updatedSorties = allSorties.slice()
        const index = updatedSorties.findIndex((s) => s.frontend_id === sortie.frontend_id)

        updatedSorties.splice(index, 1, {
            ...dialogSortie,
            // Calculate duration and add to sortie
            duration: MissionUtils.calculateSortieDuration(dialogSortie),
        })
        handleMissionDataChange({ sorties: updatedSorties })
        setCloseDialog()
    }

    const handleDeleteSortie = () => {
        const updatedSorties = allSorties.slice()
        const index = updatedSorties.findIndex((s) => s.frontend_id === sortie.frontend_id)

        updatedSorties.splice(index, 1)
        handleMissionDataChange({ sorties: updatedSorties })
        setCloseDialog()
    }

    const handleCancelSortie = () => {
        if (isCreatingSortie) {
            handleDeleteSortie()
        } else {
            setCloseDialog()
        }
    }

    return (
        <Dialog open={isEditDialogOpen} onClose={setCloseDialog}>
            <DialogTitle>Edit: Sortie {sortie.frontend_id}</DialogTitle>
            <Divider />
            <DialogContent>
                <Stack spacing={2}>
                    <Label disableGutter={true}>Time in Air (local time)</Label>
                    <NexDateTimeField
                        label="Wheels Up"
                        required={true}
                        error={!dialogSortie.fe_start_sortie}
                        value={dialogSortie.fe_start_sortie ?? ""}
                        onChange={(value) => handleUpdateSortie("fe_start_sortie", value)}
                        includeSeconds={false}
                        disableFuture={true}
                    />
                    <NexDateTimeField
                        label="Wheels Down"
                        required={true}
                        error={
                            !dialogSortie.fe_stop_sortie ||
                            (!!(dialogSortie.fe_start_sortie && dialogSortie.fe_stop_sortie) &&
                                !isAfter(dialogSortie.fe_stop_sortie, dialogSortie.fe_start_sortie))
                        }
                        value={dialogSortie.fe_stop_sortie ?? ""}
                        onChange={(value) => handleUpdateSortie("fe_stop_sortie", value)}
                        includeSeconds={false}
                        disableFuture={true}
                    />

                    <Label disableGutter={true}>Hobbs (Hours)</Label>
                    <NumberField
                        label="Start"
                        value={dialogSortie.start_hobbs}
                        required={true}
                        onChange={(value) => handleHobbsChange("start_hobbs", value ?? undefined)}
                    />
                    <NumberField
                        label="Stop"
                        value={dialogSortie.stop_hobbs}
                        required={true}
                        onChange={(value) => handleHobbsChange("stop_hobbs", value ?? undefined)}
                    />

                    <NumberField
                        label="Online"
                        value={dialogSortie.active_hobbs}
                        required={true}
                        onChange={(value) => handleHobbsChange("active_hobbs", value ?? undefined)}
                    />
                    <NumberField label="Mob" value={dialogSortie.mob_hobbs} disabled />
                </Stack>
            </DialogContent>
            <DialogActions>
                {!isCreatingSortie && (
                    <Button color="error" onClick={handleDeleteSortie}>
                        Delete
                    </Button>
                )}
                <Box flexGrow={1} />
                <Button onClick={handleCancelSortie}>Cancel</Button>
                <Button variant="contained" onClick={handleSaveSortie} disabled={!isSortieValid(dialogSortie)}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}
